import "@fontsource/open-sans/index.css";
import { createTheme } from "@mui/material/styles";
import { aderantColors } from "./aderantColors";
/** Aderant Theme Object */
const aderantTheme = createTheme({
    palette: {
        primary: {
            main: aderantColors.R2D2,
            contrastText: aderantColors.Stormtrooper
        },
        secondary: {
            main: aderantColors.Stormtrooper,
            contrastText: aderantColors.R2D2
        },
        background: {
            //Material properties
            default: aderantColors.Stormtrooper,
            //Custom properties
            header: aderantColors.Galaxy,
            footer: aderantColors.Lightsaber45,
            active: aderantColors.Ahsoka,
            focus: aderantColors.Lightsaber,
            hover: aderantColors.Lightsaber,
            disabled: aderantColors.Tauntaun,
            white: aderantColors.Stormtrooper
        },
        border: {
            //Custom properties
            active: aderantColors.Ahsoka,
            main: aderantColors.TIEFighter,
            focus: aderantColors.Ahsoka
        },
        text: {
            //Material properties
            primary: aderantColors.DarthVader,
            secondary: aderantColors.DeathStar,
            disabled: aderantColors.DeathStar,
            //Custom properties
            active: aderantColors.Ahsoka,
            hyperlink: aderantColors.R2D2,
            highlight: aderantColors.C3PO,
            black: aderantColors.DarthVader,
            white: aderantColors.Stormtrooper
        },
        divider: aderantColors.TIEFighter,
        error: {
            main: aderantColors.DarthMaul,
            light: aderantColors.Sith
        },
        info: {
            main: aderantColors.R2D2
        },
        warning: {
            main: aderantColors.BB8,
            light: aderantColors.Tatooine
        },
        success: {
            main: aderantColors.BobaFett,
            light: aderantColors.Yoda
        },
        action: {
            disabled: undefined,
            disabledBackground: undefined
        },
        controls: {
            grid: {
                rowGroup: {
                    backgroundColor: aderantColors.Lightsaber45
                },
                row: {
                    backgroundColor: aderantColors.Stormtrooper
                },
                toolbar: {
                    borderColor: aderantColors.Galaxy
                }
            },
            input: {
                border: {
                    main: aderantColors.DeathStar,
                    focus: aderantColors.Galaxy,
                    hover: aderantColors.R2D2
                }
            },
            dialog: {
                contentBackground: aderantColors.Leia
            },
            progressBar: {
                backgroundColor: aderantColors.TIEFighter,
                barColor: aderantColors.BobaFett
            }
        }
    },
    typography: {
        fontFamily: "Open Sans",
        fontSize: 14,
        button: {
            textTransform: "none",
            fontWeight: "normal"
        },
        body1: {
            fontSize: 14
        }
    },
    shape: {
        borderRadius: 6
    }
});
/** Theme style Overrides */
aderantTheme.components = {
    MuiDialog: {
        defaultProps: {
            PaperProps: { square: true }
        }
    },
    MuiFormHelperText: {
        styleOverrides: {
            root: {
                marginTop: 0,
                color: aderantTheme.palette.text.disabled,
                "&$error": {
                    color: aderantTheme.palette.error.main
                },
                "&$disabled": {
                    color: aderantColors.Leia
                },
                "&$required": {
                    color: aderantTheme.palette.warning.main
                }
            }
        }
    },
    MuiInput: {
        styleOverrides: {
            root: {
                "&$disabled": {
                    color: aderantTheme.palette.text.disabled
                }
            },
            underline: {
                color: aderantTheme.palette.controls.input.border.main
            }
        },
        defaultProps: {
            disableUnderline: true
        }
    },
    MuiInputLabel: {
        styleOverrides: {
            root: {
                whiteSpace: "nowrap",
                color: aderantTheme.palette.text.disabled,
                minWidth: "35%",
                maxWidth: "200px",
                overflow: "hidden",
                "&$disabled": {
                    color: aderantTheme.palette.text.disabled
                },
                "&$error": {
                    color: aderantTheme.palette.text.disabled
                }
            }
        }
    },
    MuiTableHead: {
        styleOverrides: {
            root: {
                backgroundColor: aderantTheme.palette.background.header,
                color: aderantTheme.palette.text.white,
                width: "100%",
                height: 40
            }
        }
    },
    MuiTableRow: {
        styleOverrides: {
            root: {
                borderBottom: `1px solid ${aderantTheme.palette.border.main}`,
                boxSizing: "border-box"
            }
        }
    },
    MuiTableCell: {
        styleOverrides: {
            root: {
                color: "inherit",
                borderBottom: 0,
                display: "table-cell",
                verticalAlign: "middle",
                padding: "0 0.7em 0 0.7em",
                overflow: "hidden",
                lineHeight: 2,
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                margin: "auto 0"
            },
            head: {
                color: aderantTheme.palette.text.white,
                lineHeight: 2,
                borderColor: aderantTheme.palette.border.main
            }
        }
    },
    MuiChip: {
        styleOverrides: {
            root: {
                borderRadius: 0,
                backgroundColor: aderantTheme.palette.background.white,
                border: `1px solid ${aderantColors.Tauntaun}`,
                color: aderantTheme.palette.text.black
            },
            deleteIconOutlinedColorPrimary: {
                color: aderantTheme.palette.primary.main
            }
        }
    },
    MuiTab: {
        styleOverrides: {
            root: {
                minWidth: "140px",
                padding: "6px 30px",
                height: "42px",
                "&:hover": {
                    backgroundColor: aderantTheme.palette.background.hover
                }
            },
            textColorPrimary: {
                color: aderantTheme.palette.primary.main,
                "&.Mui-selected": {
                    color: aderantTheme.palette.text.active
                }
            }
        }
    },
    MuiTabs: {
        styleOverrides: {
            root: {
                height: "42px"
            },
            indicator: {
                backgroundColor: aderantTheme.palette.background.active,
                height: 3
            }
        }
    },
    MuiPaper: {
        defaultProps: {
            square: true
        }
    },
    MuiMenuItem: {
        styleOverrides: {
            root: {
                "&:hover": {
                    backgroundColor: aderantTheme.palette.background.hover
                }
            }
        }
    }
};
export default aderantTheme;
